import type { Action, Agent, FieldConfig, Hook, Model } from '@lucidtech/las-sdk-browser';
import { useCallback, useMemo, useState } from 'react';

import { CREATE_PREDICTION_FUNCTION_ID, HOOK_EVALUATE_DOCUMENT } from '@/constants';
import {
  toActionId,
  toAgentId,
  toHookId,
  toModelId,
  useCreateAction,
  useCreateHook,
  useCreateModel,
  useDeleteAction,
  useDeleteHook,
  useDeleteModel,
  useUpdateAgent,
} from '@/hooks/api';

export type UseAddModelOnSuccess = ({
  model,
  action,
  hook,
  agent,
}: {
  model: Model;
  action: Action;
  hook: Hook;
  agent: Agent;
}) => void;

export type UseAddModelOnError = ({ error }: { error: Error }) => void;

export type UseAddModelOpts = {
  onSuccess?: UseAddModelOnSuccess;
  onError?: UseAddModelOnError;
};

export const useAddModel = (opts?: UseAddModelOpts) => {
  const { onSuccess: onSuccessFnBase, onError: onErrorFnBase } = opts ?? {};

  const {
    mutateAsync: updateAgent,
    isPending: isPendingUpdateAgent,
    isSuccess: isSuccessUpdateAgent,
    error: errorUpdateAgent,
  } = useUpdateAgent();

  const {
    mutateAsync: createAction,
    isPending: isPendingCreateAction,
    isSuccess: isSuccessCreateAction,
    error: errorCreateAction,
  } = useCreateAction();

  const {
    mutateAsync: createModel,
    isPending: isPendingCreateModel,
    isSuccess: isSuccessCreateModel,
    error: errorCreateModel,
  } = useCreateModel();

  const {
    mutateAsync: createHook,
    isPending: isPendingCreateHook,
    isSuccess: isSuccessCreateHook,
    error: errorCreateHook,
  } = useCreateHook();

  const { mutateAsync: deleteModel } = useDeleteModel();
  const { mutateAsync: deleteAction } = useDeleteAction();
  const { mutateAsync: deleteHook } = useDeleteHook();

  const [error, setError] = useState<Error | null>(null);

  const addModel = useCallback(
    async ({
      agentId,
      name,
      fieldConfig,
      onSuccess: onSuccessFn,
      onError: onErrorFn,
    }: {
      agentId: string;
      name?: string;
      fieldConfig: FieldConfig;
      onSuccess?: UseAddModelOnSuccess;
      onError?: UseAddModelOnError;
    }) => {
      setError(null);

      const onSuccess: UseAddModelOnSuccess = (...args) => {
        onSuccessFnBase?.(...args);
        onSuccessFn?.(...args);
      };

      const onError: UseAddModelOnError = (...args) => {
        onErrorFnBase?.(...args);
        onErrorFn?.(...args);
      };

      const cleanupFns: (() => Promise<unknown>)[] = [];

      const onCleanup = async () => {
        /* cleanup in reverse order */
        for (const fn of cleanupFns.toReversed()) {
          try {
            await fn();
          } catch (error) {
            console.error(error);
          }
        }
      };

      try {
        const model = await createModel({
          fieldConfig: fieldConfig,
          name: name,
        });

        cleanupFns.push(() => deleteModel(toModelId(model.modelId)));

        const action = await createAction({
          config: { modelId: model.modelId },
          functionId: CREATE_PREDICTION_FUNCTION_ID,
        });

        cleanupFns.push(() => deleteAction(toActionId(action.actionId)));

        const hook = await createHook({
          functionId: HOOK_EVALUATE_DOCUMENT,
          config: {},
          trigger: 'Document is Created',
          trueActionId: action.actionId,
        });

        cleanupFns.push(() => deleteHook(toHookId(hook.hookId)));

        const agent = await updateAgent({
          id: toAgentId(agentId),
          updates: (agent: Agent) => ({
            resourceIds: [...agent.resourceIds, model.modelId, action.actionId, hook.hookId],
          }),
        });

        onSuccess?.({ model, action, hook, agent });

        return model;
      } catch (e) {
        console.error(e);
        const error = e instanceof Error ? e : new Error('An unknown error occurred');

        onCleanup().finally(() => {
          setError(error);
          onError?.({ error });
        });
        throw error;
      }
    },
    [
      createAction,
      createHook,
      createModel,
      deleteAction,
      deleteHook,
      deleteModel,
      onErrorFnBase,
      onSuccessFnBase,
      updateAgent,
    ]
  );

  const errors = useMemo(() => {
    const errors = [errorCreateModel, errorCreateAction, errorCreateHook, errorUpdateAgent, error].filter((e) => !!e);
    return errors.length > 0 ? errors : undefined;
  }, [error, errorCreateAction, errorCreateHook, errorCreateModel, errorUpdateAgent]);

  return {
    addModel,
    isPending: isPendingCreateModel || isPendingCreateAction || isPendingCreateHook || isPendingUpdateAgent,
    isSuccess: isSuccessCreateModel && isSuccessCreateAction && isSuccessCreateHook && isSuccessUpdateAgent,
    errors,
  };
};
